import clsx from 'clsx';
import styles from './DrawerHeader.module.scss';
import EditableDropdown from '../../../../../common/EditableDropdown/EditableDropdown';
import FyMultiSelect from './FyMultiSelect';
import DataAvailabilityButton from './DataAvailabilityButton';
import ToleranceLegend from './ToleranceLegend';
import { caretRightPurple } from '../../../../../assets/hostedassets';
import { UNIT_OPTIONS, useUnits } from '../UnitsContext';
import { useReconExpand } from '../ReconExpandContext';
import { COLLAPSE_ALL_LABEL, EXPAND_ALL_LABEL } from '../constants/reconciliationsCopy';

interface DrawerHeaderProps {
    selectedFys: number[];
    onFysChange: (fys: number[]) => void;
    orgId: string;
    /** Same condition that renders the section cards — the pill has nothing to expand otherwise. */
    showExpandAll: boolean;
}

/** Two rows: the title row (heading, divider, tolerance legend) and the toolbar (Units, Financial
 *  Year, Expand/Collapse All, Data Availability). The close button belongs to SideDrawer, which
 *  positions it absolutely over the title row's reserved right padding. */
function DrawerHeader({ selectedFys, onFysChange, orgId, showExpandAll }: Readonly<DrawerHeaderProps>) {
    const { unit, setUnit } = useUnits();
    const { expandedCount, totalCount, toggleAll } = useReconExpand();
    const allExpanded = totalCount > 0 && expandedCount === totalCount;

    return (
        <div className={styles.DrawerHeader}>
            <div className={styles.TitleRow}>
                <h2 className={styles.Title}>Reconciliations</h2>
                <div className={styles.Divider} />
                <ToleranceLegend className={styles.LegendSlot} />
            </div>
            <div className={styles.Toolbar}>
                <div className={styles.UnitsWrapper}>
                    <span className={styles.UnitsLabel}>Units ₹:</span>
                    <EditableDropdown
                        id="reconciliations-units"
                        value={unit}
                        options={[...UNIT_OPTIONS]}
                        setOutput={(next: string) => setUnit(next)}
                        removeInput
                        wrapperStyle={{ width: 'fit-content' }}
                        inputStyle={{
                            height: '2.25rem',
                            // Fixed rather than content-fit: a plain <input> has no CSS way to track its
                            // value's rendered width, so this is sized to the longest option ("Crore"/
                            // "Lakhs") to guarantee no clipping — wider than the ~78px "INR" reads off the
                            // design screenshot, an approximation.
                            width: '5.5rem',
                            padding: '0 2rem 0 0.75rem',
                            border: '1px solid var(--border-color)',
                        }}
                    />
                </div>
                <div className={styles.FyWrapper}>
                    <span className={styles.FyLabel}>Financial Year</span>
                    <div className={styles.FyControl}>
                        <FyMultiSelect selectedFys={selectedFys} onChange={onFysChange} />
                    </div>
                </div>
                {showExpandAll && (
                    <button type="button" className={styles.ExpandAllPill} onClick={toggleAll}>
                        {allExpanded ? COLLAPSE_ALL_LABEL : EXPAND_ALL_LABEL}
                        <img
                            src={caretRightPurple}
                            alt=""
                            aria-hidden="true"
                            className={clsx(styles.ExpandAllChevron, { [styles.ExpandAllChevronCollapse]: allExpanded })}
                        />
                    </button>
                )}
                <DataAvailabilityButton orgId={orgId} className={styles.DataAvailabilityButton} />
            </div>
        </div>
    );
}

export default DrawerHeader;
